import { useEffect, useState } from "react";
import { modules } from "../config/modules";
import { settingsService } from "../services/settingsService";
import { parseSpecialDays, formatSpecialDays, SPECIAL_DAY_OPTIONS } from "../utils/rateTiers";

const ALL_ENABLED = SPECIAL_DAY_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.key]: true }),
  {}
);

export function SettingsPanel({ notify, userRole }) {
  const [settings, setSettings] = useState({});
  const [permissions, setPermissions] = useState({});
  const [specialDays, setSpecialDays] = useState(ALL_ENABLED);
  const [settingsReady, setSettingsReady] = useState(false);
  const [savingSpecialDays, setSavingSpecialDays] = useState(false);

  useEffect(() => {
    settingsService
      .getSettings()
      .then((loaded) => {
        setSettings(loaded || {});
        setSpecialDays(parseSpecialDays(loaded));
        setSettingsReady(true);
      })
      .catch((err) => {
        console.error("Settings load error:", err);
        setSettingsReady(false);
      });
    settingsService.getPermissions().then(setPermissions).catch((err) => {
      console.error("Permissions load error:", err);
    });
  }, []);

  async function saveSettings(event) {
    event.preventDefault();
    try {
      await settingsService.saveSettings(settings);
      notify("Configuration saved.");
    } catch (err) {
      console.error("Settings save error:", err);
      notify(err?.message || "Failed to save configuration.");
    }
  }

  async function savePermissions() {
    try {
      await settingsService.savePermissions(permissions);
      notify("Permissions saved.");
    } catch (err) {
      console.error("Permissions save error:", err);
      notify(err?.message || "Failed to save permissions.");
    }
  }

  async function saveSpecialDays() {
    setSavingSpecialDays(true);
    try {
      const next = { ...settings, ...formatSpecialDays(specialDays) };
      await settingsService.saveSettings(next);
      setSettings(next);
      notify("Special days saved.");
    } catch (err) {
      console.error("Special days save error:", err);
      notify(err?.message || "Failed to save special days.");
    } finally {
      setSavingSpecialDays(false);
    }
  }

  const placeholders = {
    Property: "e.g. Cottage1",
    Status: "Available, Booked, Maintenance",
    RoomType: "Regular, Standard, Deluxe",
    Addons: "AC, Breakfast, Meal",
    BedType: "Quad, Bed Occupancy",
    BlockMin: "30",
  };

  const fields = ["Property", "Status", "RoomType", "Addons", "BedType", "BlockMin"];

  return (
    <section className="stack two-col">
      <form className="panel form-stack" onSubmit={saveSettings}>
        <h2 style={{ background: "linear-gradient(to right, #1e40af, #312e81)", color: "white", padding: "10px 14px", borderRadius: "6px", marginBottom: 24 }}>Core System Parameters</h2>
        {fields.map((key) => (
          <label key={key}>{key}<input placeholder={placeholders[key]} value={settings[key] || (key === "BlockMin" ? "30" : "")} onChange={(event) => setSettings({ ...settings, [key]: event.target.value })} /></label>
        ))}
        <button className="primary" disabled={!settingsReady}>Save Configuration</button>
        <p className="image-hint">
          Weekend days, holidays and peak season dates are configured per property on the
          Property page.
        </p>
      </form>
      <div className="right-col-stack">
        <div className="panel form-stack">
          <h2>Role Permissions</h2>
          {Object.entries(permissions)
            .filter(([role]) => {
              // If logged in user is tenant, only show Admin, Agent, Staff
              if (userRole && (userRole.toLowerCase() === "tenant" || userRole.toLowerCase() === "owner")) {
                return ["Admin", "Agent", "Staff"].includes(role);
              }
              return true;
            })
            .map(([role, allowed]) => {
              const allowedModules = Array.isArray(allowed) ? allowed : [];
              return (
              <div className="permission-row" key={role}>
                <strong>{role}</strong>
                <div>
                  {modules.map((module) => (
                    <label key={module.id}>
                      <input type="checkbox" checked={allowedModules.includes(module.id)} onChange={(event) => {
                        const next = event.target.checked ? [...allowedModules, module.id] : allowedModules.filter((id) => id !== module.id);
                        setPermissions({ ...permissions, [role]: next });
                      }} />
                      {module.label}
                    </label>
                  ))}
                </div>
              </div>
              );
            })}
          <button className="primary" onClick={savePermissions}>Save Permissions</button>
        </div>

        <div className="panel form-stack">
          <h2>Special Days</h2>
          <div className="special-day-list">
            {SPECIAL_DAY_OPTIONS.map((option) => (
              <label className="special-day-row" key={option.key}>
                <input
                  type="checkbox"
                  checked={Boolean(specialDays[option.key])}
                  onChange={(event) =>
                    setSpecialDays({ ...specialDays, [option.key]: event.target.checked })
                  }
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          <button
            className="primary"
            disabled={!settingsReady || savingSpecialDays}
            onClick={saveSpecialDays}
          >
            {savingSpecialDays ? "Saving..." : "Save Special Days"}
          </button>
          <p className="image-hint">
            Uncheck a special day to stop it attracting a seasonal rate. Disabled days are
            priced at the base rate, and a property can still override its weekend days on
            the Property page.
          </p>
        </div>
      </div>
    </section>
  );
}
